import { isInvalid } from '../type/isInvalid';
// 复用 formatNumber 的工具
import {} from /* formatNumber, */ './formatNumber';

// 复制过来，避免循环依赖
function convertToNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }
    // 支持带 % 的字符串，如 '1.2345%'
    const cleanValue = trimmed.replace(/%$/, '');
    const num = Number(cleanValue);
    return isNaN(num) ? null : num;
  }
  // 只允许 number 和 string，其它类型直接返回 null
  return null;
}

function roundToPrecision(value: number, precision: number): number {
  if (precision === 0) {
    return Math.round(value);
  }
  const multiplier = Math.pow(10, precision);
  return Math.round(value * multiplier) / multiplier;
}

/**
 * 格式化百分位数值
 *
 * 将数字格式化为百分比形式，支持正负号显示、乘以100处理等功能。
 * 结果保留2位小数，使用严格四舍五入算法。当输入值无效时返回 '--'。
 *
 * @param value - 需要格式化的数字，传入非法类型会返回 '--'
 * @param showSign - 正数时是否携带+号，默认携带，0时不加+
 * @param mul100 - 数值是否需要先乘以100再进行处理，默认会先乘100
 * @returns 格式化后的百分比字符串，保留2位小数
 *
 * @example
 * ```ts
 * import { formatPercent } from '@fu/matrix';
 *
 * // 基本用法
 * formatPercent(0.1234); // => '+12.34%'
 * formatPercent(-0.1234); // => '-12.34%'
 *
 * // 不显示正号
 * formatPercent(0.1234, false); // => '12.34%'
 *
 * // 不乘以100（输入已经是百分比形式）
 * formatPercent(12.34, true, false); // => '+12.34%'
 *
 * // 处理无效输入
 * formatPercent(null); // => '--'
 * formatPercent('invalid'); // => '--'
 * formatPercent('12.34.56'); // => '--' (无效数字格式)
 * ```
 *
 * @since 1.0.0
 */
export function formatPercent(
  value: unknown,
  showSign: boolean = true,
  mul100: boolean = true,
): string {
  if (isInvalid(value)) {
    return '--';
  }
  const num = convertToNumber(value);
  if (num === null || !isFinite(num)) {
    return '--';
  }
  // 乘以100
  const percentValue = mul100 ? num * 100 : num;
  // 严格四舍五入到2位小数
  const rounded = roundToPrecision(percentValue, 2);
  // 格式化为字符串，保留2位小数
  let formatted = rounded.toFixed(2);
  // 处理0的特殊情况
  if (rounded === 0) {
    formatted = '0.00';
  }
  // 添加正负号
  let result = formatted;
  if (rounded > 0 && showSign) {
    result = '+' + result;
  }
  // 负数自动带负号，0不加+
  // 添加百分号
  return result + '%';
}